import { useFimSubjectsByIds } from 'features/fim/hooks'
import type { DocumentSubject } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import { useMemo } from 'react'

import { subjectPhotoEntries } from './photoEntry'
import { useSiteAttachedPhotos } from './useSiteAttachedPhotos'

/**
 * Photo counts of the drawing's subjects that have any photo, by the same
 * join the subject panel uses. The drawing's own subject list only carries
 * the aliases boxed on that page, so a photo elected to a subject's other
 * spelling would never match against it; the full records are fetched
 * instead, and only once the site has any attached photo at all.
 */
export function useSubjectPhotoCounts(
  siteID: string,
  documentSubjects: readonly DocumentSubject[]
): ReadonlyMap<string, number> {
  const { photos } = useSiteAttachedPhotos(siteID)
  const uids = useMemo(
    () =>
      photos.length > 0
        ? documentSubjects.map((subject) => subject.subject_uid)
        : undefined,
    [documentSubjects, photos.length]
  )
  const { subjects } = useFimSubjectsByIds(siteID, uids)

  return useMemo(
    () =>
      new Map(
        subjects.flatMap((subject) => {
          const count = subjectPhotoEntries(subject, photos).length
          return count > 0 ? [[subject.subjectUid, count] as const] : []
        })
      ),
    [photos, subjects]
  )
}
